import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { COMMON_FAULTS } from "@/lib/faults";

export default function SettingsPage() {
  const { meta, can, user } = useAuth();
  const [stores, setStores] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", surname: "", email: "", phone: "", role: "clerk", store_id: "", password: "" });
  const [doc, setDoc] = useState({ document_footer: "", warranty_text: "" });
  const [parts, setParts] = useState([]);
  const [faultMap, setFaultMap] = useState({});
  const [pwdUser, setPwdUser] = useState(null);
  const [pwd, setPwd] = useState("");

  const load = () => {
    api.get("/stores").then((r) => setStores(r.data));
    if (can("users.view")) api.get("/users").then((r) => setUsers(r.data));
    api.get("/parts").then((r) => setParts(r.data)).catch(() => {});
    api.get("/settings").then((r) => {
      setDoc({ document_footer: r.data.document_footer || "", warranty_text: r.data.warranty_text || "" });
      setFaultMap(r.data.fault_part_map || {});
    });
  };
  useEffect(() => { load(); }, []);

  const partName = (id) => parts.find((p) => p.id === id)?.name || id;
  const addPartToFault = (fault, pid) => setFaultMap((m) => ({ ...m, [fault]: [...(m[fault] || []), pid] }));
  const removePartFromFault = (fault, pid) => setFaultMap((m) => ({ ...m, [fault]: (m[fault] || []).filter((x) => x !== pid) }));
  const saveFaultMap = async () => {
    try { await api.put("/settings/fault-parts", { fault_part_map: faultMap }); toast.success("Mappatura salvata"); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const saveDoc = async () => {
    try { await api.put("/settings/documents", doc); toast.success("Impostazioni documenti salvate"); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const delUser = async (u) => {
    if (!window.confirm(`Eliminare l'utente ${u.name} ${u.surname}?`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("Utente eliminato"); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const savePwd = async () => {
    try { await api.patch(`/users/${pwdUser.id}`, { password: pwd }); toast.success("Password aggiornata"); setPwdUser(null); setPwd(""); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const create = async () => {
    try {
      await api.post("/users", { ...f, store_id: f.store_id || null });
      toast.success("Utente creato"); setOpen(false);
      setF({ name: "", surname: "", email: "", phone: "", role: "clerk", store_id: "", password: "" }); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <h1 className="font-heading text-2xl font-extrabold tracking-tight sm:text-3xl">Impostazioni</h1>
      <Tabs defaultValue="stores">
        <TabsList>
          <TabsTrigger value="stores" data-testid="set-tab-stores">Negozi</TabsTrigger>
          <TabsTrigger value="users" data-testid="set-tab-users">Utenti</TabsTrigger>
          <TabsTrigger value="roles" data-testid="set-tab-roles">Ruoli</TabsTrigger>
          <TabsTrigger value="documents" data-testid="set-tab-documents">Documenti</TabsTrigger>
          {user?.role === "admin" && <TabsTrigger value="parts" data-testid="set-tab-parts">Ricambi</TabsTrigger>}
        </TabsList>

        <TabsContent value="stores" className="space-y-3 pt-4">
          {stores.map((s) => (
            <Card key={s.id} className="flex items-center justify-between p-5">
              <div><div className="font-semibold">{s.name}</div><div className="text-xs text-muted-foreground">{s.city}</div></div>
              <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-semibold text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">Attivo</span>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="users" className="pt-4 space-y-4">
          {user?.role === "admin" && <Button data-testid="new-user-btn" onClick={() => setOpen(true)}>Nuovo utente</Button>}
          <Card className="overflow-hidden">
            <div className="divide-y divide-border">
              {users.map((u) => (
                <div key={u.id} className="flex items-center gap-4 px-5 py-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 font-semibold text-primary">{u.name[0]}</span>
                  <div className="flex-1"><div className="font-medium">{u.name} {u.surname}</div><div className="text-xs text-muted-foreground">{u.email}</div></div>
                  <span className="rounded-full bg-secondary px-2.5 py-0.5 text-xs">{u.role_label}</span>
                  {user?.role === "admin" && (
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" data-testid={`user-pwd-${u.id}`} onClick={() => { setPwdUser(u); setPwd(""); }}>Password</Button>
                      {u.id !== user.id && (
                        <Button size="sm" variant="outline" data-testid={`user-del-${u.id}`} onClick={() => delUser(u)}
                          className="text-red-600 hover:bg-red-50 hover:text-red-700 dark:hover:bg-red-950/30">Elimina</Button>
                      )}
                    </div>
                  )}
                </div>
              ))}
              {users.length === 0 && <div className="px-5 py-10 text-center text-muted-foreground">Nessun utente visibile.</div>}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="roles" className="pt-4 space-y-3">
          {Object.entries(meta.roles || {}).map(([code, label]) => (
            <Card key={code} className="p-5">
              <div className="font-semibold">{label}</div>
              <div className="text-xs font-mono text-muted-foreground">{code}</div>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="documents" className="pt-4 space-y-4">
          <Card className="p-5 space-y-4">
            <div>
              <Label>Nota a piè di pagina</Label>
              <p className="mb-2 text-xs text-muted-foreground">Testo breve stampato in fondo a ogni documento (es. ringraziamento, orari).</p>
              <Input data-testid="doc-footer" value={doc.document_footer} onChange={(e) => setDoc({ ...doc, document_footer: e.target.value })} placeholder="Grazie per aver scelto Dexter Lab" />
            </div>
            <div>
              <Label>Condizioni e garanzia</Label>
              <p className="mb-2 text-xs text-muted-foreground">Stampate nella scheda riparazione.</p>
              <Textarea data-testid="doc-warranty" rows={4} value={doc.warranty_text} onChange={(e) => setDoc({ ...doc, warranty_text: e.target.value })} placeholder="Garanzia di 3 mesi sui ricambi sostituiti..." />
            </div>
            <Button data-testid="save-doc-btn" onClick={saveDoc}>Salva</Button>
          </Card>
        </TabsContent>

        <TabsContent value="parts" className="pt-4 space-y-3">
          <Card className="p-5 space-y-4">
            <p className="text-sm text-muted-foreground">Associa i guasti frequenti ai ricambi di magazzino: verranno proposti in automatico nel preventivo.</p>
            {COMMON_FAULTS.map((fault) => (
              <div key={fault} className="space-y-2 border-b border-border pb-3 last:border-0" data-testid={`faultmap-${fault}`}>
                <div className="text-sm font-medium">{fault}</div>
                <div className="flex flex-wrap items-center gap-2">
                  {(faultMap[fault] || []).map((pid) => (
                    <span key={pid} className="flex items-center gap-1 rounded-full bg-secondary px-2.5 py-0.5 text-xs">
                      {partName(pid)}
                      <button type="button" data-testid={`faultmap-remove-${pid}`} onClick={() => removePartFromFault(fault, pid)} className="text-muted-foreground hover:text-foreground">×</button>
                    </span>
                  ))}
                  <Select value="" onValueChange={(v) => addPartToFault(fault, v)}>
                    <SelectTrigger className="h-8 max-w-[240px]" data-testid={`faultmap-select-${fault}`}><SelectValue placeholder="Aggiungi ricambio…" /></SelectTrigger>
                    <SelectContent>
                      {parts.filter((p) => !(faultMap[fault] || []).includes(p.id)).map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ))}
            <Button data-testid="save-faultmap-btn" onClick={saveFaultMap}>Salva mappatura</Button>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nuovo utente</DialogTitle></DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1"><Label>Nome *</Label><Input data-testid="user-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
            <div className="space-y-1"><Label>Cognome</Label><Input value={f.surname} onChange={(e) => setF({ ...f, surname: e.target.value })} /></div>
            <div className="space-y-1"><Label>Email *</Label><Input data-testid="user-email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></div>
            <div className="space-y-1"><Label>Password *</Label><Input data-testid="user-password" type="text" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} /></div>
            <div className="space-y-1"><Label>Ruolo</Label>
              <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}>
                <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(meta.roles || {}).map(([c, l]) => <SelectItem key={c} value={c}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label>Negozio</Label>
              <Select value={f.store_id} onValueChange={(v) => setF({ ...f, store_id: v })}>
                <SelectTrigger data-testid="user-store"><SelectValue placeholder="Nessuno (tutti)" /></SelectTrigger>
                <SelectContent>{stores.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button data-testid="save-user-btn" onClick={create}>Crea utente</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pwdUser} onOpenChange={(o) => { if (!o) setPwdUser(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Cambia password{pwdUser ? ` — ${pwdUser.name} ${pwdUser.surname}` : ""}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label>Nuova password *</Label>
            <Input data-testid="new-password-input" type="text" value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder="Almeno 6 caratteri" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwdUser(null)}>Annulla</Button>
            <Button data-testid="save-password-btn" disabled={pwd.length < 6} onClick={savePwd}>Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
